import React, { useState, SyntheticEvent } from 'react'
import {
  Flex,
  Icon,
  Details,
  Button,
  Confirmation,
  HRLocationSelect,
  DepartmentSelect,
  EmployeeTypeSelect,
  EmployeeSelect,
  Toaster,
} from '@ui'
import { InputGroup, Position, Intent, FormGroup } from '@blueprintjs/core'
import { DateInput2 } from '@blueprintjs/datetime2'

import { DateUtil, TIME, datePickerFormatter } from '@utils'
import api, { IEmployeeDetails, FormErrors, IDepartment, IEmployee, IHRLocation } from '@api'
import { get, omit } from 'lodash'
// import moment from 'moment'
import useSanitize from '@hooks/useSanitize'
import { ApolloError } from 'apollo-boost'

export interface IFormEmployee {
  id?: string
  name: string
  email: string
  title: string
  employmentType: string
  badge: string
  phone: string | null
  geoLocation: IHRLocation | null
  department: IDepartment | null
  manager: IEmployee | null
  startDate: Date | null
  endDate: Date | null
}

interface EmployeeFormProps {
  dirty: boolean
  setDirty: (dirty: boolean) => void
  employee: IFormEmployee
  onCancel: () => void
  onSubmit: (employee: IEmployeeDetails) => void
}

interface HRLocationChange {
  name: 'geoLocation'
  value: IHRLocation
}

interface DepartmentChange {
  name: 'department' | 'team'
  value: IDepartment
}

interface ManagerChange {
  name: 'manager'
  value: IEmployee
}

interface EmploymentTypeChange {
  name: 'employmentType'
  value: string
}

interface IRequiredField {
  key: 'name' | 'email' | 'geoLocation' | 'department'
  label: string
}

const requiredFields: IRequiredField[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'geoLocation', label: 'HR Location' },
  { key: 'department', label: 'Department' },
]

type EmployeeFormErrors = FormErrors<IFormEmployee>

function isValid(employee: IFormEmployee) {
  let valid = true
  requiredFields.forEach(({ key }) => {
    if (valid) {
      valid = !!employee[key]
    }
  })

  return valid
}

export default function EmployeeForm({
  employee: initState,
  dirty,
  setDirty,
  onCancel,
  onSubmit,
}: EmployeeFormProps) {
  const [employee, setEmployee] = useState<IFormEmployee>(initState)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<EmployeeFormErrors>({})
  const sanitize = useSanitize()

  const onTextInputChange = ({
    currentTarget: { name, value },
  }: SyntheticEvent<HTMLInputElement>) => {
    setDirty(true)
    setErrors(omit(errors, name))
    setEmployee({
      ...employee,
      [name]: value,
    })
  }

  const onSelectValueChange = ({
    name,
    value,
  }: HRLocationChange | DepartmentChange | ManagerChange | EmploymentTypeChange) => {
    setDirty(true)
    setErrors(omit(errors, name))
    setEmployee({
      ...employee,
      [name]: value,
    })
  }

  const onDateChange = ({ name, value }: { name: 'startDate' | 'endDate'; value: Date | null }) => {
    setDirty(true)
    setEmployee({
      ...employee,
      [name]: value,
    })
  }

  const handleErrors = ({ kind, graphQLErrors }: any) => {
    setLoading(false)

    if (graphQLErrors && graphQLErrors.length) {
      const newErrors: EmployeeFormErrors = graphQLErrors.reduce(
        (result: EmployeeFormErrors, { invalidArguments }: any) => {
          return Object.assign(
            result,
            Object.entries(invalidArguments).reduce(
              (result, [key, value]) => ({
                ...result,
                [key]: value,
              }),
              {}
            )
          )
        },
        {}
      )

      setErrors(newErrors)
    }
  }

  const highlightErrors = () => {
    const foundErrors: EmployeeFormErrors = {}
    requiredFields.forEach((field) => {
      if (!employee[field.key]) {
        foundErrors[field.key] = `${field.label} is required`
      }
    })
    if (Object.keys(foundErrors).length) {
      setErrors((prevState) => ({ ...prevState, ...foundErrors }))
    }
  }

  const columns = [
    [
      {
        label: 'Name',
        required: true,
        formEl: (
          <FormGroup helperText={errors.name} intent={errors.name ? Intent.DANGER : Intent.NONE}>
            <InputGroup
              autoFocus
              placeholder="John Smith"
              name="name"
              data-input="name"
              intent={errors.name ? Intent.DANGER : Intent.NONE}
              value={employee.name}
              onChange={onTextInputChange}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Email',
        required: true,
        formEl: (
          <FormGroup helperText={errors.email} intent={errors.email ? Intent.DANGER : Intent.NONE}>
            <InputGroup
              placeholder="john.smith@mail.com"
              type="email"
              name="email"
              intent={errors.email ? Intent.DANGER : Intent.NONE}
              value={employee.email}
              onChange={onTextInputChange}
            />
          </FormGroup>
        ),
      },
      {
        label: 'HR Location',
        required: true,
        formEl: (
          <FormGroup
            helperText={errors.geoLocation}
            intent={errors.geoLocation ? Intent.DANGER : Intent.NONE}>
            <HRLocationSelect
              value={employee.geoLocation}
              onChange={(geoLocation) => {
                onSelectValueChange({
                  name: 'geoLocation',
                  value: geoLocation,
                })
              }}
              buttonProps={{
                hasError: errors.geoLocation,
              }}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Department/Team',
        required: true,
        formEl: (
          <FormGroup
            helperText={errors.department}
            intent={errors.department ? Intent.DANGER : Intent.NONE}>
            <DepartmentSelect
              value={employee.department}
              onChange={(department) =>
                onSelectValueChange({
                  name: 'department',
                  value: department as IDepartment,
                })
              }
              buttonProps={{
                hasError: errors.department,
                'data-button': 'department_select',
              }}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Title',
        formEl: (
          <FormGroup helperText={errors.title} intent={errors.title ? Intent.DANGER : Intent.NONE}>
            <InputGroup
              placeholder="Project Manager"
              name="title"
              value={employee.title}
              onChange={onTextInputChange}
              intent={errors.title ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Phone',
        formEl: (
          <FormGroup helperText={errors.phone} intent={errors.phone ? Intent.DANGER : Intent.NONE}>
            <InputGroup
              placeholder="1(555)-555-5555"
              title="phone"
              name="phone"
              value={employee.phone || ''}
              onChange={onTextInputChange}
              intent={errors.phone ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>
        ),
      },
    ],
    [
      {
        label: 'Manager',
        formEl: (
          <FormGroup
            helperText={errors.manager}
            intent={errors.manager ? Intent.DANGER : Intent.NONE}>
            <EmployeeSelect
              clearable
              value={employee.manager}
              onChange={(manager) =>
                onSelectValueChange({
                  name: 'manager',
                  value: manager as IEmployee,
                })
              }
              buttonProps={{ hasError: errors.manager }}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Type',
        formEl: (
          <FormGroup
            helperText={errors.employmentType}
            intent={errors.employmentType ? Intent.DANGER : Intent.NONE}>
            <EmployeeTypeSelect
              value={employee.employmentType}
              onChange={(employmentType) =>
                onSelectValueChange({
                  name: 'employmentType',
                  value: employmentType as string,
                })
              }
              buttonProps={{
                hasError: errors.employmentType,
              }}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Badge',
        formEl: (
          <FormGroup helperText={errors.badge} intent={errors.badge ? Intent.DANGER : Intent.NONE}>
            <InputGroup
              placeholder="0000001"
              name="badge"
              value={employee.badge === null ? '' : employee.badge}
              onChange={onTextInputChange}
              intent={errors.badge ? Intent.DANGER : Intent.NONE}
            />
          </FormGroup>
        ),
      },
      {
        label: 'Start Date',
        formEl: (
          <FormGroup
            helperText={errors.startDate}
            intent={errors.startDate ? Intent.DANGER : Intent.NONE}>
            <DateInput2
              popoverProps={{ minimal: true, position: Position.BOTTOM }}
              inputProps={{
                leftIcon: 'calendar',
                placeholder: 'MM/DD/YY',
              }}
              {...datePickerFormatter('MM/DD/YY')}
              maxDate={employee.endDate || DateUtil().add(20, TIME.YEARS).toDate()}
              value={employee.startDate ? DateUtil(employee.startDate).toString() : null}
              onChange={(startDate) =>
                onDateChange({ name: 'startDate', value: startDate ? DateUtil(startDate as string).toDate() : null })
              }
            />
          </FormGroup>
        ),
      },
      {
        label: 'End Date',
        formEl: (
          <FormGroup
            helperText={errors.endDate}
            intent={errors.endDate ? Intent.DANGER : Intent.NONE}>
            <DateInput2
              popoverProps={{ minimal: true, position: Position.BOTTOM }}
              inputProps={{
                leftIcon: 'calendar',
                placeholder: 'MM/DD/YY',
              }}
              {...datePickerFormatter('MM/DD/YY')}
              value={employee.endDate ? DateUtil(employee.endDate).toString() : null}
              minDate={employee.startDate ? employee.startDate : undefined}
              maxDate={DateUtil().add(20, TIME.YEARS).toDate()}
              onChange={(endDate) =>
                onDateChange({
                  name: 'endDate',
                  value: endDate ? DateUtil(endDate as unknown as string).toDate() : null,
                })
              }
            />
          </FormGroup>
        ),
      },
    ],
  ]

  return (
    <div style={{ paddingTop: 20 }}>
      <Details
        onSubmit={(e: SyntheticEvent<HTMLFormElement>) => {
          e.preventDefault()
          if (!isValid(employee)) {
            highlightErrors()
            return
          }

          setLoading(true)

          const startDate = employee.startDate
            ? DateUtil(employee.startDate).format('YYYY-MM-DD')
            : null

          const endDate = employee.endDate ? DateUtil(employee.endDate).format('YYYY-MM-DD') : null

          if (employee.id) {
            api.employee
              .update({
                id: employee.id,
                name: sanitize(employee.name),
                title: sanitize(employee.title),
                email: sanitize(employee.email),
                phone: employee.phone,
                employmentType: employee.employmentType,
                startDate,
                endDate,
                badge: employee.badge ? employee.badge.trim() : '',
                managerId: get(employee, 'manager.id', null),
                departmentId: get(employee, 'department.id', null),
                geoLocationId: get(employee, 'geoLocation.id', null),
              })
              .then((updatedEmployee) => onSubmit(updatedEmployee))
              .then(() =>
                Toaster.success({
                  message: 'Employee updated',
                })
              )
              .catch((error: ApolloError) => {
                handleErrors(error)
                Toaster.fail({ error })
              })
          } else {
            api.employee
              .create({
                name: sanitize(employee.name),
                title: sanitize(employee.title),
                email: sanitize(employee.email),
                phone: employee.phone as string,
                employmentType: employee.employmentType,
                startDate,
                endDate,
                badge: employee.badge ? employee.badge.trim() : '',
                managerId: get(employee, 'manager.id', ''),
                departmentId: get(employee, 'department.id', ''),
                geoLocationId: get(employee, 'geoLocation.id', ''),
              })
              .then((newEmployee) => onSubmit(newEmployee))
              .then(() => {
                Toaster.success({
                  message: 'New employee created',
                })
              })
              .catch((error: ApolloError) => {
                handleErrors(error)
                Toaster.fail({
                  error,
                })
              })
          }
        }}>
        <Details.Columns>
          {columns.map((rows, i) => (
            <Details.Column key={i} style={i === 0 ? { width: 582 } : { width: 300 }}>
              {rows.map(({ label, required, formEl }) => (
                <Details.Row key={label}>
                  <Details.Label {...{ required, label }} />
                  {formEl}
                </Details.Row>
              ))}
            </Details.Column>
          ))}
        </Details.Columns>
        <Details.Footer>
          <Flex>
            <div style={{ marginRight: 8 }}>
              {dirty ? (
                <Confirmation text="Discard unsaved changes?" onConfirm={onCancel}>
                  <Button type="button" text="Cancel" />
                </Confirmation>
              ) : (
                <Button type="button" text="Cancel" onClick={onCancel} />
              )}
            </div>
            <Button
              type="submit"
              disabled={!dirty}
              loading={loading}
              intent={Intent.PRIMARY}
              data-button={employee.id ? 'update_employee' : 'create_employee'}
              text={employee.id ? 'Update' : 'Create'}
            />
          </Flex>
        </Details.Footer>
      </Details>
    </div>
  )
}
